import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardFilterBar } from '@/features/dashboard-filters';
import { useHeaderFilters, useHeaderSlot } from '@/features/header-filters';
import { useProjectSiteParams } from '@/shared/lib';

import { useConsignmentsFilters } from '../lib/use-consignments-filters';
import { ConsignmentsPage } from './consignments-page';
import { ConsignmentsViewpointToggle } from './consignments-viewpoint-toggle';

export function ConsignmentsWithFilters() {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const { setFilterConfig, setSearchConfig } = useHeaderFilters();

  const {
    urlState,
    allSites,
    searchQuery,
    activeFiltersCount,
    organizationOptions,
    setOrganizationSearch,
    setSelectedOrganization,
    handleFilterChange,
    handleSearchChange,
    handleDateRangeChange,
    handleReset,
    handleApplyFilters,
    handleToggleAllSites,
    isFiltersDirty,
    hasDefaultFilters,
    activeFilters
  } = useConsignmentsFilters();

  const viewpointToggle = useMemo(
    () => <ConsignmentsViewpointToggle allSites={allSites} onChange={handleToggleAllSites} />,
    [allSites, handleToggleAllSites]
  );

  useHeaderSlot('actions', viewpointToggle);

  useEffect(() => {
    const config = {
      defaultValue: searchQuery,
      placeholder: t('cmrlist.filters.search.placeholder', 'Search consignments...'),
      onChange: handleSearchChange,
      enabled: true
    };
    setSearchConfig(config);
  }, [handleSearchChange, setSearchConfig, t, searchQuery]);

  useEffect(() => {
    const filterContent = (
      <DashboardFilterBar
        variant="consignments"
        options={urlState}
        onOptionChange={handleFilterChange}
        onDateRangeChange={handleDateRangeChange}
        consignmentNumber={urlState.requestNumber}
        onConsignmentNumberChange={(value) => handleFilterChange('requestNumber', value)}
        organizationOptions={organizationOptions}
        selectedOrganization={urlState.orgId}
        onOrganizationChange={setSelectedOrganization}
        onOrganizationSearchChange={setOrganizationSearch}
        waybill={urlState.waybill}
        onWaybillChange={(value) => handleFilterChange('waybill', value)}
        resetKey={`${projectId}-${siteId}`}
      />
    );

    setFilterConfig({
      id: 'consignments-filters',
      title: t('cmrlist.filters.title', 'Filter Consignments'),
      content: filterContent,
      activeCount: activeFiltersCount,
      activeFilters,
      onReset: handleReset,
      onApply: handleApplyFilters,
      onClearAll: handleReset,
      isApplyDisabled: !isFiltersDirty,
      isResetDisabled: hasDefaultFilters
    });
  }, [
    activeFiltersCount,
    activeFilters,
    handleFilterChange,
    handleDateRangeChange,
    handleReset,
    handleApplyFilters,
    isFiltersDirty,
    hasDefaultFilters,
    setFilterConfig,
    setSearchConfig,
    projectId,
    siteId,
    t,
    urlState,
    organizationOptions,
    setSelectedOrganization,
    setOrganizationSearch
  ]);

  return <ConsignmentsPage />;
}
